import { authSetCognitoToken } from "slices/authSlice";
import { refreshCognitoToken } from "services/cognitoService";
import { getStoreFrontDesignImages } from "services/storeFront";
import { store } from "store";
import { isExpiredAuthTokenError } from "utils/errorUtils";
import { isInIframe } from "utils/iframeUtils";
import { publish } from "utils/messages";

const TOKEN_WAIT_TIMEOUT_MS = 3000;

const waitForCognitoToken = (timeoutMs: number): Promise<string | undefined> =>
  new Promise((resolve) => {
    const currentToken = store.getState()?.auth?.cognitoToken?.token;
    if (currentToken) {
      resolve(currentToken);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      unsubscribe();
      resolve(undefined);
    }, timeoutMs);

    const unsubscribe = store.subscribe(() => {
      const token = store.getState()?.auth?.cognitoToken?.token;
      if (token) {
        window.clearTimeout(timeoutId);
        unsubscribe();
        resolve(token);
      }
    });
  });

const ensureFreshCognitoToken = async (): Promise<string | undefined> => {
  const refreshed = await refreshCognitoToken();
  if (refreshed?.token) {
    store.dispatch(authSetCognitoToken(refreshed));
    return refreshed.token;
  }

  if (isInIframe()) {
    publish.queryCognitoToken();
    return waitForCognitoToken(TOKEN_WAIT_TIMEOUT_MS);
  }

  return undefined;
};

const getStoreFrontDesignImagesWithAuthRetry = async (
  jwt: string,
  designId: string,
  designVersion: number,
  envOverride?: string
): Promise<any> => {
  try {
    return await getStoreFrontDesignImages(
      jwt,
      designId,
      designVersion,
      envOverride
    );
  } catch (error) {
    if (!isExpiredAuthTokenError(error)) {
      throw error;
    }

    const refreshedToken = await ensureFreshCognitoToken();
    if (!refreshedToken) {
      throw error;
    }

    return getStoreFrontDesignImages(
      refreshedToken,
      designId,
      designVersion,
      envOverride
    );
  }
};

export {
  ensureFreshCognitoToken,
  getStoreFrontDesignImagesWithAuthRetry,
  TOKEN_WAIT_TIMEOUT_MS,
};
